import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { GraduationCap, Camera, CheckCircle2, XCircle, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
const GOLD_INK = '#8F650F';

const STATUS_META = {
  freigeschaltet: { label: 'Freigeschaltet — bereit zum Start', color: GOLD_INK, Icon: GraduationCap },
  laufend: { label: 'Läuft — jetzt fortsetzen', color: '#E07A5F', Icon: Clock },
  abgegeben: { label: 'Abgegeben — wird geprüft', color: GOLD_INK, Icon: CheckCircle2 },
};

// Prüfungs-Karte im Kurs — sichtbar sobald der Admin eine Prüfung freigeschaltet hat
export const ExamCard = ({ courseId, getAuthHeader }) => {
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/user/exams?course_id=${courseId}`, getAuthHeader());
      const list = r.data.attempts || [];
      setAttempt(list.length ? list[0] : null);
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  if (!attempt) return null;

  const isReviewed = attempt.status === 'bewertet';
  const passed = attempt.result?.outcome === 'bestanden';
  const meta = isReviewed
    ? (passed
      ? { label: 'Bestanden', color: '#2F7A3D', Icon: CheckCircle2 }
      : { label: 'Nicht bestanden', color: '#A83232', Icon: XCircle })
    : (STATUS_META[attempt.status] || STATUS_META.freigeschaltet);
  const { Icon } = meta;

  return (
    <div className="mb-8 bg-cream-100 border border-cream-300 rounded-2xl p-5 sm:p-6" data-testid="exam-card">
      <p className="font-mono text-[9.5px] uppercase font-semibold mb-1.5" style={{ letterSpacing: '0.22em', color: GOLD_INK }}>
        Abschlussprüfung
      </p>
      <h3 className="font-display text-[19px] text-espresso leading-snug mb-1">{attempt.exam_title}</h3>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color: meta.color }} />
        <p className="text-[13px] font-semibold" style={{ color: meta.color }} data-testid="exam-card-status">{meta.label}</p>
      </div>
      {attempt.result?.feedback && (
        <p className="text-[12.5px] italic text-sand-700 mb-3">Feedback: {attempt.result.feedback}</p>
      )}
      {['freigeschaltet', 'laufend'].includes(attempt.status) && (
        <>
          <p className="text-[13px] text-sand-600 leading-relaxed mb-4 flex items-center gap-1.5">
            <Camera className="w-3.5 h-3.5" />
            {attempt.camera_required ? 'Mit Kamera-Aufsicht' : 'Kamera-Ausnahme aktiviert'} · Zeitlimit {attempt.duration_minutes} Min.
          </p>
          <button
            onClick={() => navigate(`/exam/${attempt.id}`)}
            className="w-full sm:w-auto px-6 py-3 rounded-full text-[12px] font-bold uppercase"
            style={{ backgroundColor: '#E07A5F', color: '#FBF7F0', letterSpacing: '0.14em' }}
            data-testid="exam-card-enter"
          >
            {attempt.status === 'laufend' ? 'Prüfung fortsetzen' : 'Prüfungsraum betreten'}
          </button>
        </>
      )}
      {['abgegeben', 'bewertet'].includes(attempt.status) && attempt.result?.percent !== undefined && attempt.result?.percent !== null && (
        <p className="text-[12.5px] text-sand-600" data-testid="exam-card-points">
          Ergebnis: {attempt.result.total} von {attempt.result.total_max} Punkten ({attempt.result.percent} %)
        </p>
      )}
    </div>
  );
};

export default ExamCard;
